import { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';

import { AgeGroup } from '@/shared/api/protocol-ts/api/core/svc_patient_pb';
import { trackEvent } from '@/shared/lib';
import { DropdownSelect } from '@/shared/ui';
import { OptionType as Option } from '@/shared/ui/Select/Select.types';

import { ageGroupMessages, AGE_GROUP_TO_LABEL } from '../config';

import styles from './DateOfBirthDropdownFilter.module.scss';

type DateOfBirthDropdownFilterProps = {
  onChange?: (values: string[]) => void;
};

export const DateOfBirthDropdownFilter: FC<DateOfBirthDropdownFilterProps> = ({
  onChange,
}) => {
  const { formatMessage } = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFilters = searchParams.get('ageGroup')?.split(',') || [
    AgeGroup.PatientListStreamAgeGroup_AllAges.toString(),
  ];
  const [filters, setFilters] = useState<string[]>(initialFilters);

  const selectedQuantity = filters.filter(
    (filter) =>
      filter !== AgeGroup.PatientListStreamAgeGroup_AllAges.toString(),
  ).length;

  const options = useMemo(
    (): Option[] => [
      {
        value: AgeGroup.PatientListStreamAgeGroup_Adults.toString(),
        label: formatMessage(ageGroupMessages.adults),
      },
      {
        value: AgeGroup.PatientListStreamAgeGroup_Children.toString(),
        label: formatMessage(ageGroupMessages.children),
      },
    ],
    [formatMessage],
  );

  const handleChange = (value: string | string[]) => {
    if (typeof value === 'string') {
      return;
    }

    setFilters(value);
  };

  const handleApply = (value: string | string[]) => {
    if (typeof value === 'string') {
      return;
    }

    const filterLabels = value.map(
      (ageGroup) => AGE_GROUP_TO_LABEL[ageGroup] || ageGroup,
    );

    trackEvent({
      eventName: 'PatientsPageDOBFilterApplyButtonClicked',
      properties: {
        filter: filterLabels.join(','),
      },
    });

    if (onChange) {
      onChange(value);
    }
    searchParams.set('ageGroup', value.join(','));
    setSearchParams(searchParams);
  };

  const handleClear = () => {
    setFilters([]);
    if (onChange) {
      onChange([]);
    }
    searchParams.delete('ageGroup');
    setSearchParams(searchParams);
  };

  return (
    <DropdownSelect
      isMulti
      value={filters}
      label={formatMessage({
        id: 'patientList.dateOfBirth',
        defaultMessage: 'Date of birth',
      })}
      options={options}
      className={styles.container}
      stylesOptions={{ listMaxHeight: 206, hideSearch: true }}
      icon="filter"
      multiValuesCount={selectedQuantity > 0 ? selectedQuantity : undefined}
      onChange={handleChange}
      onApply={handleApply}
      onClear={handleClear}
    />
  );
};
